import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiGet, apiPost } from '@/lib/http/client';

export type SeatFeature = 'ai_actions' | 'chat' | 'crm_export';

// GET /api/orgs/<id>/seat-upgrade-request/ — user response
interface SeatUpgradeRequestState {
  pending: boolean;
}

// POST /api/orgs/<id>/seat-upgrade-request/ — always returns status
interface SeatUpgradeRequestCreateResponse {
  status: 'created' | 'already_pending';
}

// One per org so admin views and user views share cache.
const queryKey = (orgId: number) =>
  ['orgs', orgId, 'seat-upgrade-request'] as const;

/**
 * Fetches and posts seat upgrade requests for the current user's membership.
 *
 * GET /api/orgs/<id>/seat-upgrade-request/ → { pending: boolean }
 * POST /api/orgs/<id>/seat-upgrade-request/ → { status: "created" | "already_pending" }
 *
 * On a successful POST (or a pre-existing pending request reported by GET),
 * `hasPendingRequest` returns true and the CTA is disabled.
 */
export function useSeatUpgradeRequest(orgId: number | null | undefined) {
  const queryClient = useQueryClient();
  const enabled = orgId != null;

  const query = useQuery({
    queryKey: queryKey(orgId ?? 0),
    queryFn: () =>
      apiGet<SeatUpgradeRequestState>(
        `/api/orgs/${orgId}/seat-upgrade-request/`
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      apiPost<SeatUpgradeRequestCreateResponse>(
        `/api/orgs/${orgId}/seat-upgrade-request/`,
        {}
      ),
    onSuccess: () => {
      // Mark the query stale so the GET refreshes and shows the chip.
      queryClient.setQueryData(queryKey(orgId ?? 0), { pending: true });
    },
  });

  const requestUpgrade = useCallback(() => {
    if (!enabled) {
      return;
    }
    mutation.mutate();
  }, [enabled, mutation]);

  const hasPendingRequest =
    query.data?.pending === true ||
    mutation.data?.status === 'created' ||
    mutation.data?.status === 'already_pending';

  return {
    hasPendingRequest,
    requestUpgrade,
    isLoading: query.isLoading,
    isSubmitting: mutation.isPending,
    isError: mutation.isError,
  };
}

// ---------------------------------------------------------------------------
// Admin view: fetch all pending seat-upgrade requests for a given org so the
// TeamManagementTab can show the chip beside affected viewer rows.
// ---------------------------------------------------------------------------

interface SeatUpgradeRequestItem {
  membership_id: number;
  email: string;
  created_at: string;
}

interface AdminSeatUpgradeRequestsState {
  pending_requests: SeatUpgradeRequestItem[];
}

const adminQueryKey = (orgId: number) =>
  ['orgs', orgId, 'seat-upgrade-request', 'admin'] as const;

/**
 * Admin-only: lists memberships with a pending seat upgrade request.
 * The backend returns the admin response shape when the session user holds the
 * `ahoy_other_admin` permission group.
 * Only fetches when `enabled` is true (i.e. when the session user is an admin).
 */
export function useAdminSeatUpgradeRequests(
  orgId: number | null | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: adminQueryKey(orgId ?? 0),
    queryFn: () =>
      apiGet<AdminSeatUpgradeRequestsState>(
        `/api/orgs/${orgId}/seat-upgrade-request/`
      ),
    enabled: enabled && orgId != null,
    staleTime: 5 * 60 * 1000,
  });
}
